import React, { useEffect, useState } from 'react'
import { Box, Button, Card, IconButton, InputAdornment, MenuItem, TextField, Typography } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import { ReadinessChip } from '../../merchants/shared/StateChip'
import { C, SX, TYPE } from '../../merchants/shared/tokens'
import { errorText, fetchZoneList } from '../ptlSetupApi'
import { NOT_SERVICEABLE, ODA_REGULAR, ODA_TYPES, fmtDate, num } from '../constants'

const YES_NO = [{ value: '', label: 'All' }, { value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]

/** Filter keys of GET zone-map/list, all strings ('' = not set). */
export const EMPTY_ZONE_FILTERS = { pincodes: '', zoneId: '', state: '', city: '', oda: '', greenTax: '', specialZone: '' }

/** Text filters wait for the user to stop typing before they reach the URL and the server. */
const TYPING_MS = 400

const zoneLabel = (id, name) => (id === NOT_SERVICEABLE ? '9999 · Not serviceable' : `${id}${name ? ` · ${name}` : ''}`)

/**
 * Pincodes of a PTL zone map, one row each, server-paged, with the filters of the requirement doc (pincodes several at
 * once, zone, state, city, ODA type, green tax, special zone). Filters are controlled by the page, so KPI cards and zone
 * tiles can set them too.
 *
 * @param merchantId  null = standard map; a merchant = its own pincodes (adds Standard zone and Last change columns)
 * @param zones       [{ zoneId, name }] for the Zone filter
 * @param filters     EMPTY_ZONE_FILTERS shape; onFilters(next) sets them
 * @param actions     buttons for the list header (download, upload ...)
 */
export default function ZoneListing({ merchantId, zones, filters, onFilters, refreshKey, actions }) {
  const [draft, setDraft] = useState(filters)
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(50)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [listError, setListError] = useState('')
  const standard = !merchantId

  // The page can change filters (KPI card, zone tile): take them over.
  useEffect(() => { setDraft(filters); setPage(0) }, [filters])

  useEffect(() => {
    const same = Object.keys(EMPTY_ZONE_FILTERS).every((k) => draft[k] === filters[k])
    if (same) return undefined
    const t = setTimeout(() => onFilters(draft), TYPING_MS)
    return () => clearTimeout(t)
  }, [draft, filters, onFilters])

  useEffect(() => {
    let live = true
    setLoading(true)
    setListError('')
    fetchZoneList({ merchantId, ...filters, page, size })
      .then((d) => live && setData(d))
      // A failed load must not leave the previous rows on screen as if they matched the filters.
      .catch((e) => { if (live) { setData({ rows: [], total: 0 }); setListError(errorText(e, 'Could not load pincodes')) } })
      .finally(() => live && setLoading(false))
    return () => { live = false }
  }, [merchantId, filters, page, size, refreshKey])

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }))
  const filtered = Object.values(filters).some((v) => v !== '')
  // Several pincodes searched at once: say which are not in this map instead of dropping them silently.
  const asked = [...new Set((filters.pincodes.match(/\d{6}/g) || []))]
  const found = new Set((data?.rows || []).map((r) => String(r.pincode)))
  const notInMap = asked.length && data && data.total <= size ? asked.filter((p) => !found.has(p)) : []

  const columns = [
    { field: 'pincode', headerName: 'Pincode', width: 95, sortable: false },
    { field: 'city', headerName: 'City', flex: 1, minWidth: 120, sortable: false },
    { field: 'state', headerName: 'State', flex: 1, minWidth: 120, sortable: false },
    { field: 'region', headerName: 'Region', width: 120, sortable: false },
    {
      field: 'zoneId', headerName: 'Zone', width: 180, sortable: false,
      renderCell: ({ row }) => (row.zoneId === NOT_SERVICEABLE
        ? <ReadinessChip tone='blocked' label='9999 · Not serviceable' />
        : <Typography sx={{ fontSize: 13, color: C.inkBody }}>{zoneLabel(row.zoneId, row.zoneName)}</Typography>),
    },
    ...(standard ? [] : [{
      field: 'standardZoneId', headerName: 'Standard zone', width: 125, sortable: false,
      renderCell: ({ row }) => (
        <Typography sx={{ fontSize: 13, color: row.standardZoneId === row.zoneId ? C.inkFaint : C.warn }}>{row.standardZoneId || 'Not in map'}</Typography>
      ),
    }]),
    { field: 'greenTax', headerName: 'Green tax', width: 95, sortable: false, renderCell: ({ value }) => (value ? 'Yes' : '') },
    { field: 'specialZone', headerName: 'Special zone', width: 110, sortable: false, renderCell: ({ value }) => (value ? 'Yes' : '') },
    {
      field: 'oda', headerName: 'ODA type', width: 110, sortable: false,
      renderCell: ({ value }) => (value && value !== ODA_REGULAR
        ? <ReadinessChip tone='pending' label={value} />
        : <Typography sx={{ fontSize: 13, color: C.inkMuted }}>{value || ODA_REGULAR}</Typography>),
    },
    {
      field: 'updatedAt', headerName: 'Last changed', width: 190, sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.5 }}>
          <Typography sx={{ fontSize: 12, color: C.inkBody }}>{fmtDate(row.updatedAt) || '—'}</Typography>
          {row.updatedBy && <Typography sx={{ fontSize: 11, color: C.inkFaint }}>{row.updatedBy}</Typography>}
        </Box>
      ),
    },
  ]

  return (
    <Card elevation={0} sx={{ ...SX.card, overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${C.line}`, bgcolor: C.surfaceSunken }}>
        <TextField size='small' label='Pincodes' placeholder='One or more, comma separated' value={draft.pincodes} sx={{ width: { xs: '100%', sm: 260 }, bgcolor: C.surface }}
          onChange={(e) => set('pincodes', e.target.value.replace(/[^0-9, \n]/g, ''))}
          InputProps={{
            endAdornment: draft.pincodes ? (
              <InputAdornment position='end'>
                <IconButton size='small' aria-label='Clear pincodes' edge='end' onClick={() => set('pincodes', '')}><ClearIcon fontSize='small' /></IconButton>
              </InputAdornment>
            ) : null,
          }} />
        <TextField select size='small' label='Zone' value={draft.zoneId} sx={{ width: 180, bgcolor: C.surface }} onChange={(e) => set('zoneId', e.target.value)}>
          <MenuItem value=''>All</MenuItem>
          {(zones || []).map((z) => <MenuItem key={z.zoneId} value={z.zoneId}>{zoneLabel(z.zoneId, z.name)}</MenuItem>)}
          <MenuItem value={NOT_SERVICEABLE}>{zoneLabel(NOT_SERVICEABLE)}</MenuItem>
        </TextField>
        <TextField size='small' label='State' value={draft.state} sx={{ width: 150, bgcolor: C.surface }} onChange={(e) => set('state', e.target.value)} />
        <TextField size='small' label='City' value={draft.city} sx={{ width: 150, bgcolor: C.surface }} onChange={(e) => set('city', e.target.value)} />
        <TextField select size='small' label='ODA type' value={draft.oda} sx={{ width: 130, bgcolor: C.surface }} onChange={(e) => set('oda', e.target.value)}>
          <MenuItem value=''>All</MenuItem>
          {ODA_TYPES.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
        <TextField select size='small' label='Green tax' value={draft.greenTax} sx={{ width: 120, bgcolor: C.surface }} onChange={(e) => set('greenTax', e.target.value)}>
          {YES_NO.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <TextField select size='small' label='Special zone' value={draft.specialZone} sx={{ width: 140, bgcolor: C.surface }} onChange={(e) => set('specialZone', e.target.value)}>
          {YES_NO.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        {filtered && <Button size='small' onClick={() => onFilters(EMPTY_ZONE_FILTERS)} sx={{ textTransform: 'none', fontWeight: 600 }}>Clear filters</Button>}
      </Box>
      <Box sx={{ px: 2, py: 1.25, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, borderBottom: `1px solid ${C.line}` }}>
        <Typography role='status' sx={{ ...TYPE.value, color: listError ? C.danger : C.inkMuted, fontVariantNumeric: 'tabular-nums' }}>
          {listError || (data ? `${num(data.total)} pincode${data.total === 1 ? '' : 's'}${filtered ? ` match${data.total === 1 ? 'es' : ''} the filters` : ''}` : 'Loading pincodes…')}
          {!listError && notInMap.length > 0 && ` · not in this map: ${notInMap.join(', ')}`}
        </Typography>
        {actions && <Box sx={{ ml: 'auto', display: 'flex', flexWrap: 'wrap', gap: 1 }}>{actions}</Box>}
      </Box>
      <ProzoDataGrid columns={columns} rows={data?.rows || []} rowCount={data?.total || 0} paginationMode='server' pagination
        page={page} setPage={setPage} sizePerPage={size} setSizePerPage={(s) => { setSize(s); setPage(0) }}
        rowsPerPageOptions={[20, 50, 100, 200]} loading={loading} getRowId={(r) => r.pincode} />
    </Card>
  )
}
